import { TrendingDown, TrendingFlat, TrendingUp } from "@mui/icons-material";
import { Typography } from "@mui/material";

/**
 * Displays a year-over-year comparison badge with a trending icon and percentage.
 *
 * - Green TrendingUp for increases
 * - Red TrendingDown for decreases
 * - Grey TrendingFlat when unchanged
 */
export const SLYearOverYearBadge = ({
  current,
  previous,
  label,
  short = false,
}: {
  /** Current period value */
  current: number;
  /** Previous period value */
  previous: number;
  /** Contextual label, e.g. "Vorjahr (2023)" or "Januar 2023" */
  label: string;
  /** If true, show compact text without "gegenüber {label}" */
  short?: boolean;
}) => {
  // Nothing to compare
  if (previous <= 0 && current <= 0) return null;

  const diff = current - previous;

  // Previous was 0 but current is positive → new entry, no percentage possible
  const isNew = previous <= 0 && current > 0;
  const diffPercent = previous > 0 ? Math.round((diff / previous) * 100) : null;

  const text = short
    ? isNew
      ? "Neu"
      : diff !== 0 && diffPercent !== null
        ? `${diff > 0 ? "+" : ""}${diffPercent}%`
        : "Unverändert"
    : isNew
      ? `Neu gegenüber ${label}`
      : diff !== 0 && diffPercent !== null
        ? `${Math.abs(diffPercent)}% ${diff > 0 ? "Zunahme" : "Abnahme"} gegenüber ${label}`
        : `Keine Veränderung gegenüber ${label}`;

  const color = isNew
    ? "info.main"
    : diff > 0
      ? "rgb(56, 142, 60)"
      : diff < 0
        ? "rgb(211, 47, 47)"
        : "rgb(117, 117, 117)";

  return (
    <Typography
      variant="subtitle1"
      sx={{
        color,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        pt: 0.5,
      }}
    >
      {!isNew && (
        <>
          {diff > 0 ? (
            <TrendingUp sx={{ fontSize: "1rem" }} />
          ) : diff < 0 ? (
            <TrendingDown sx={{ fontSize: "1rem" }} />
          ) : (
            <TrendingFlat sx={{ fontSize: "1rem" }} />
          )}
        </>
      )}
      {text}
    </Typography>
  );
};
